import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { FunctionComponent, SVGProps } from "react";
import {
    faClipboardList,
    faFlag,
    faGear,
    faGlobeAmericas,
    faLocationDot,
    faRecycle,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { faExcavatorSolid } from "../icons/faExcavatorSolid";
import FroomNavCustomersHubSvg from "../icons/hub/FroomNavCustomersHub.svg?react";
import FroomNavVehicleHubSvg from "../icons/hub/FroomNavVehicleHub.svg?react";
import FroomNavHauliersHubSvg from "../icons/hub/FroomNavHauliersHub.svg?react";
import FroomNavTippersHubSvg from "../icons/hub/FroomNavTippersHub.svg?react";
import FroomNavProcessorsHubSvg from "../icons/hub/FroomNavProcessorsHub.svg?react";
import FroomNavEwcCodesHubSvg from "../icons/hub/FroomNavEwcCodesHub.svg?react";
import FroomNavPhysicalStatesHubSvg from "../icons/hub/FroomNavPhysicalStatesHub.svg?react";
import FroomNavPreTreatmentHubSvg from "../icons/hub/FroomNavPreTreatmentHub.svg?react";
import FroomNavSourceActivityHubSvg from "../icons/hub/FroomNavSourceActivityHub.svg?react";

export const HUB_WELCOME_DESCRIPTION = "Welcome. Use the sidebar or the shortcuts below.";

/** SVGR default export: inline SVG with `fill="currentColor"` so hub styling (e.g. `text-primary`) applies. */
export type HubInlineSvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

/** Hub card leading art: Font Awesome, inline SVG (SVGR), or a public URL under `public/`. */
export type HubCardGraphic =
    | { kind: "fontawesome"; icon: IconDefinition }
    | { kind: "inlineSvg"; Svg: HubInlineSvgComponent }
    | { kind: "svg"; src: string; alt?: string };

export function hubFa(icon: IconDefinition): HubCardGraphic {
    return { kind: "fontawesome", icon };
}

/** Inline SVG from `*.svg?react` (vite-plugin-svgr); use `currentColor` in the asset for theme colour. */
export function hubInlineSvg(Svg: HubInlineSvgComponent): HubCardGraphic {
    return { kind: "inlineSvg", Svg };
}

/** `src` is served from `public/` (Vite/CRA base URL). */
export function hubSvg(src: string, alt?: string): HubCardGraphic {
    return { kind: "svg", src, alt };
}

const dashboardIcon = "/icons/froom-nav-dashboard.svg";
const dashboardIconSelected = "/icons/froom-nav-dashboard-selected.svg";
const settingsIcon = "/icons/froom-nav-settings.svg";
const settingsIconSelected = "/icons/froom-nav-settings-selected.svg";
const operationsIcon = "/icons/froom-nav-operations.svg";
const operationsIconSelected = "/icons/froom-nav-operations-selected.svg";
const movementIcon = "/icons/froom-nav-movement-parties.svg";
const movementIconSelected = "/icons/froom-nav-movement-parties-selected.svg";
const refDataIcon = "/icons/froom-nav-ref-data.svg";
const refDataIconSelected = "/icons/froom-nav-ref-data-selected.svg";

export type SidebarNavItem = {
    name: string;
    path: string;
    icon: string;
    selectedIcon: string;
    roles?: string[];
};

/** Top-level sidebar entries (order preserved). */
export const sidebarNavItems: SidebarNavItem[] = [
    { name: "Dashboard", path: "/dashboard", icon: dashboardIcon, selectedIcon: dashboardIconSelected },
    { name: "Operations", path: "/operations", icon: operationsIcon, selectedIcon: operationsIconSelected },
    { name: "Movement & Parties", path: "/movement-parties", icon: movementIcon, selectedIcon: movementIconSelected },
    { name: "Reference Data", path: "/reference-data", icon: refDataIcon, selectedIcon: refDataIconSelected },


    { name: "System", path: "/system", icon: settingsIcon, selectedIcon: settingsIconSelected },
];

export type HubCardDef = {
    title: string;
    to: string;
    description: string;
    icon: HubCardGraphic;
};

export const operationsHubCards: HubCardDef[] = [
    { title: "Sites", to: "/sites", description: "View and manage known sites", icon: hubFa(faExcavatorSolid) },
    { title: "Customers", to: "/customers", description: "View and manage customers", icon: hubInlineSvg(FroomNavCustomersHubSvg) },
    { title: "Locations", to: "/locations", description: "View and manage known locations", icon: hubFa(faLocationDot) },
];

export const movementPartiesHubCards: HubCardDef[] = [
    { title: "Vehicles", to: "/vehicles", description: "View and manage known vehicles", icon: hubInlineSvg(FroomNavVehicleHubSvg) },
    { title: "Hauliers", to: "/hauliers", description: "View and manage hauliers", icon: hubInlineSvg(FroomNavHauliersHubSvg) },
    { title: "Tippers", to: "/tippers", description: "View and manage tippers", icon: hubInlineSvg(FroomNavTippersHubSvg) },
    { title: "Processors", to: "/processors", description: "View and manage processors", icon: hubInlineSvg(FroomNavProcessorsHubSvg) },
];

export const referenceDataHubCards: HubCardDef[] = [
    { title: "Waste Types", to: "/waste-types", description: "View and manage waste types", icon: hubFa(faTrash) },
    { title: "EWC Codes", to: "/ewc-codes", description: "View and manage EWC codes", icon: hubInlineSvg(FroomNavEwcCodesHubSvg) },
    {
        title: "Disposal/Recovery Codes",
        to: "/disposal-recovery-codes",
        description: "View and manage disposal and recovery codes",
        icon: hubFa(faRecycle),
    },
    { title: "Physical States", to: "/physical-states", description: "View and manage physical state values", icon: hubInlineSvg(FroomNavPhysicalStatesHubSvg) },
    { title: "Pre-Treatment", to: "/pre-treatment", description: "View and manage pre-treatment values", icon: hubInlineSvg(FroomNavPreTreatmentHubSvg) },
    { title: "Source Activity", to: "/source-activity", description: "View and manage source activity values", icon: hubInlineSvg(FroomNavSourceActivityHubSvg) },
    { title: "Geography", to: "/geography", description: "View and manage geography values", icon: hubFa(faGlobeAmericas) },
    {
        title: "Flags (Municipal / Degradable)",
        to: "/flags",
        description: "View and manage reporting flags",
        icon: hubFa(faFlag),
    },
];

export const systemHubCards: HubCardDef[] = [
    { title: "Settings", to: "/settings", description: "View and manage system settings", icon: hubFa(faGear) },
    { title: "Audit Logs", to: "/audit-logs", description: "View system audit logs", icon: hubFa(faClipboardList) },
];

export const operationsChildPaths = ["/operations", "/sites", "/customers", "/locations"] as const;

export const movementPartiesChildPaths = [
    "/movement-parties",
    "/vehicles",
    "/hauliers",
    "/tippers",
    "/processors",
] as const;

export const referenceDataChildPaths = [
    "/reference-data",
    "/waste-types",
    "/ewc-codes",
    "/disposal-recovery-codes",
    "/physical-states",
    "/pre-treatment",
    "/source-activity",
    "/geography",
    "/flags",
] as const;

/** Exact paths that must highlight the System sidebar parent (admin / configuration). */
const systemAdminExactPaths = new Set<string>([
    "/settings",
    "/users",
    "/organisations",
    "/company",
    "/websitesettings",
    "/administration",
    "/invitations",
    "/sessions",
    "/checklists",
]);

const systemAdminPathPrefixes = ["/EmailTemplates"];

export function isSystemAdminPath(pathname: string): boolean {
    if (systemAdminExactPaths.has(pathname)) {
        return true;
    }
    return systemAdminPathPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function isSystemNavActive(pathname: string): boolean {
    if (pathname === "/system" || pathname === "/audit-logs") {
        return true;
    }
    return isSystemAdminPath(pathname);
}

export function getSidebarActiveParentPath(pathname: string): string | null {
    if (pathname === "/dashboard") {
        return "/dashboard";
    }
    if (isSystemNavActive(pathname)) {
        return "/system";
    }
    if (operationsChildPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
        return "/operations";
    }
    if (movementPartiesChildPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
        return "/movement-parties";
    }
    if (referenceDataChildPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
        return "/reference-data";
    }
    return null;
}

export function isSidebarNavItemActive(itemPath: string, pathname: string): boolean {
    const active = getSidebarActiveParentPath(pathname);
    return active !== null && active === itemPath;
}
